import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Clock, CreditCard, Shield, Users, Key, ArrowRight, Zap, CalendarClock, FileCheck2 } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SEO from '../components/SEO';
import LocalBusinessSchema from '../components/LocalBusinessSchema';
import StickyBrochureButton from '../components/StickyBrochureButton';
import BackToTopButton from '../components/BackToTopButton';
import FAQSection from '../components/FAQSection';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hero, heroOverlayOpacity } = useSiteSettings();

  return (
    <div className="min-h-screen bg-[#F9F9F7] font-body" data-testid="landing-page">
      <SEO titleKey="seo.home.title" descriptionKey="seo.home.description" />
      <LocalBusinessSchema />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#1A3C34] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={hero}
            alt="Espace professionnel"
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0 bg-black"
            style={{ opacity: (heroOverlayOpacity ?? 50) / 100 }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 lg:pt-36 pb-16 sm:pb-20 lg:pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/20 border border-[#C77D63]/30 rounded-full text-sm font-medium text-[#C77D63] mb-8 backdrop-blur-sm hero-fade-in" style={{ animationDelay: '50ms' }}>
              <Zap className="w-4 h-4" />
              {t('hero.address')}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight hero-fade-in" style={{ animationDelay: '150ms' }}>
              {t('hero.title2')}
              <br />
              <span className="text-white">{t('hero.title3')}</span>
            </h1>
            <p className="mt-5 text-xl sm:text-2xl text-white font-heading font-normal hero-fade-in" style={{ animationDelay: '220ms' }}>
              {t('hero.tagline')}
            </p>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl font-body hero-fade-in" style={{ animationDelay: '300ms' }}>
              {t('hero.description')}
            </p>
            <div className="mt-10 hero-fade-in" style={{ animationDelay: '400ms' }}>
              <button
                data-testid="hero-cta-plan"
                onClick={() => navigate('/plan')}
                className="bg-[#C77D63] text-white hover:bg-[#b06a51] transition-all rounded-lg font-bold px-8 py-4 text-base shadow-lg shadow-[#C77D63]/25 inline-flex items-center justify-center gap-2"
              >
                👉 {t('hero.cta1')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="bg-white border-b border-[#E2E2DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 text-sm text-black/75">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C77D63]" />
              <span>{t('trust.securePayment')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#C77D63]" />
              <span>{t('trust.accessByEmail')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C77D63]" />
              <span>{t('trust.instantBooking')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#C77D63]" />
              <span>{t('trust.cardBancontact')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS (inverted colors: white bg, black text) ===== */}
      <section className="bg-white py-20" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-black text-center tracking-tight">
            {t('landing.howItWorks')}
          </h2>
          <p className="mt-3 text-center text-black/70 text-base">
            {t('landing.howItWorksSubtitle')}
          </p>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '1', icon: MapPin,        title: t('landing.step1Title'), desc: t('landing.step1Desc') },
              { step: '2', icon: CalendarClock, title: t('landing.step2Title'), desc: t('landing.step2Desc') },
              { step: '3', icon: CreditCard,    title: t('landing.step3Title'), desc: t('landing.step3Desc') },
              { step: '4', icon: Key,           title: t('landing.step4Title'), desc: t('landing.step4Desc') },
            ].map((item, i) => (
              <div key={i} className="text-center group" data-testid={`how-step-${i + 1}`}>
                <div className="relative w-20 h-20 mx-auto mb-5">
                  <span className={`step-halo absolute inset-0 rounded-full bg-[#C77D63]/25 blur-xl`} aria-hidden="true" />
                  <div className="relative w-20 h-20 rounded-2xl bg-[#C77D63]/15 flex items-center justify-center ring-1 ring-[#C77D63]/20 group-hover:bg-[#C77D63]/25 transition-colors">
                    <item.icon className={`w-8 h-8 text-[#C77D63] step-anim-${i + 1}`} />
                  </div>
                </div>
                <div className="text-xs font-bold text-[#C77D63] uppercase tracking-widest mb-2">{t('landing.step')} {item.step}</div>
                <h3 className="font-heading text-xl font-bold text-black">{item.title}</h3>
                <p className="mt-2 text-sm text-black/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES GRID ===== */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-black text-center tracking-tight">
            {t('landing.whyTitle')}
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: FileCheck2, title: t('landing.f1Title'), desc: t('landing.f1Desc') },
              { icon: Clock, title: t('landing.f2Title'), desc: t('landing.f2Desc') },
              { icon: CreditCard, title: t('landing.f3Title'), desc: t('landing.f3Desc') },
              { icon: Key, title: t('landing.f4Title'), desc: t('landing.f4Desc') },
              { icon: Users, title: t('landing.f5Title'), desc: t('landing.f5Desc') },
              { icon: Shield, title: t('landing.f6Title'), desc: t('landing.f6Desc') },
            ].map((feat, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E2E2DF] p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-[#C77D63]/15 flex items-center justify-center mb-4">
                  <feat.icon className="w-5 h-5 text-[#C77D63]" />
                </div>
                <h3 className="font-heading text-base font-bold text-black">{feat.title}</h3>
                <p className="mt-2 text-sm text-black/70 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LOCATION MAP ===== */}
      <section className="py-20 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-black tracking-tight">
                {t('landing.findUs')}
              </h2>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#C77D63] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">{t('landing.addrLine1')}</p>
                    <p className="text-sm text-black/70">{t('landing.addrLine2')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#C77D63] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">{t('landing.open7d')}</p>
                    <p className="text-sm text-black/70">{t('landing.openSchedule')}</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Flexi-location+304+Chauss%C3%A9e+de+Stockel+1200+Bruxelles"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex bg-white border border-[#E2E2DF] text-black hover:bg-[#F0EFEA] hover:border-[#C77D63] transition-colors rounded-lg font-semibold px-6 py-4 text-sm items-center justify-center gap-2"
                  data-testid="cta-google-maps"
                >
                  <MapPin className="w-4 h-4 text-[#C77D63]" />
                  {t('landing.viewOnGoogleMaps', 'Voir sur Google Maps')}
                </a>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-[#E2E2DF] h-[350px]">
              <iframe
                title="Localisation Flexi-location sur Google Maps"
                src="https://www.google.com/maps?q=304+Chauss%C3%A9e+de+Stockel,+1200+Woluwe-Saint-Lambert&z=16&output=embed"
                className="w-full h-full"
                frameBorder="0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ (moved below "Nous trouver") ===== */}
      <FAQSection />

      {/* ===== STICKY BROCHURE BUTTON (bottom-right, appears after scroll) ===== */}
      <StickyBrochureButton />
      {/* ===== BACK TO TOP (bottom-left) ===== */}
      <BackToTopButton />
    </div>
  );
}
